/**
 * Daily dungeon — one tougher, solo-boss encounter per account per day.
 * Entering commits the day's attempt (win or lose); combat.js reads
 * state.dungeonRun to spawn the dungeon's boss instead of the normal map
 * pack, apply the Protection Amulet's damage reduction, and grant the
 * guaranteed-rarity reward (or fail the run) on kill/death.
 *
 * @module core/dungeon
 */

import { dungeonRemainingMs } from './inventory.js';

/**
 * Whether a dungeon can be entered right now: the account's daily cooldown
 * has elapsed and at least one active hero meets its level requirement.
 * @param {object} state
 * @param {object} dungeon a dungeons.json entry
 * @returns {boolean}
 */
export function canEnterDungeon(state, dungeon) {
  if (!dungeon) return false;
  if (dungeonRemainingMs(state) > 0) return false;
  if (state.dungeonRun) return false; // already mid-run
  return (state.party ?? []).some((h) => h.alive && h.level >= dungeon.minLevel);
}

/**
 * Enter a dungeon: commits the daily cooldown immediately, optionally spends
 * a Protection Amulet, and arms combat.js to spawn the dungeon boss on the
 * next tick.
 * @param {object} state
 * @param {{dungeons:object}} data
 * @param {string} dungeonId key in dungeons.json
 * @param {boolean} [useAmulet=false]
 * @returns {boolean} true if the run started
 */
export function enterDungeon(state, data, dungeonId, useAmulet = false) {
  const dungeon = data.dungeons?.[dungeonId];
  if (!canEnterDungeon(state, dungeon)) return false;

  const hasAmulet = useAmulet && (state.consumables.protectionAmulets ?? 0) > 0;
  if (hasAmulet) state.consumables.protectionAmulets -= 1;

  state.dungeon.availableAt = Date.now() + 24 * 3600 * 1000;
  state.dungeonRun = { dungeonId, protected: hasAmulet };
  state.currentMonsters = []; // next ensureMonsters() spawns the dungeon boss
  return true;
}
